/**
 * RGM Scheduled Programming Hub — Phase 1 event-state helpers.
 * JSX-free CommonJS module so Jest can `require` it directly from
 * tests without going through the component's babel pipeline.
 * LivePage.js imports via ESM — babel handles the interop.
 *
 * Status rules (CEO-locked):
 *   now  <  start_at             → "upcoming"
 *   start_at ≤ now < end_at      → "live"
 *   now  ≥  end_at               → "ended"
 *   Legacy event (end_at absent) → "legacy" — preserves prior UX so
 *     cards never show a wrong "Event ended" label; the server's
 *     upcoming-scope filter removes legacy events once start_at has
 *     passed, matching the pre-correction behaviour exactly.
 * Boundaries are inclusive at start_at, exclusive at end_at, so an
 * event clicks over to LIVE the instant its scheduled time arrives.
 *
 * useEventClock(intervalMs):
 *   A tiny client-side ticker that lets rendered event cards walk
 *   UPCOMING → LIVE NOW → ENDED without the viewer reloading /live
 *   and WITHOUT refetching /api/live/events. We only re-derive the
 *   client's "now" and let computeEventStatus do the rest.
 */

const React = require("react");

function computeEventStatus(ev, now) {
  if (now === undefined) now = Date.now();
  if (!ev) return "legacy";
  const startMs = ev.start_at ? new Date(ev.start_at).getTime() : NaN;
  const endMs = ev.end_at ? new Date(ev.end_at).getTime() : NaN;
  const hasStart = Number.isFinite(startMs);
  const hasEnd = Number.isFinite(endMs);
  if (hasStart && now < startMs) return "upcoming";
  if (hasEnd && now >= endMs) return "ended";
  if (hasStart && hasEnd && startMs <= now && now < endMs) return "live";
  return "legacy";
}

// RSVP + Sharing — an RGM Studio event the host has explicitly started
// (Start Live rewrote broadcast_target and stamped started_room_at).
function hasBroadcastStarted(ev) {
  const bt = ev && ev.broadcast_target ? ev.broadcast_target : null;
  return !!(ev && ev.started_room_at && bt && bt.kind === "livekit_room" && bt.room_id);
}

function isRsvpOpen(ev, status) {
  const bt = ev && ev.broadcast_target ? ev.broadcast_target : null;
  return !!(bt && bt.kind === "rgm_studio_pending" && !ev.is_cancelled && status !== "ended");
}

function resolveJoinAction(ev, status) {
  const bt = ev && ev.broadcast_target ? ev.broadcast_target : null;
  if (status !== "ended" && hasBroadcastStarted(ev)) {
    return { kind: "livekit_room", href: "/beams/" + bt.room_id, label: "\uD83D\uDD34 Join Live", disabled: false };
  }
  // Default (and legacy fallback) — the external_url path.
  let kind = "external";
  let href = (ev && ev.external_url) || "#";
  let label = "\uD83D\uDD34 Join Live";
  let disabled = false;

  if (bt && bt.kind === "livekit_room" && bt.room_id) {
    kind = "livekit_room";
    href = "/beams/" + bt.room_id;
    label = "\uD83C\uDFA7 Join Live Room";
  } else if (bt && bt.kind === "external" && bt.url) {
    kind = "external";
    href = bt.url;
  } else if (bt && bt.kind === "broadcast_studio") {
    return {
      kind: "broadcast_studio",
      href: "#",
      label: "\uD83C\uDFAC Broadcast Studio (coming soon)",
      disabled: true,
    };
  } else if (bt && bt.kind === "rgm_studio_pending") {
    // Reserved RGM Studio slot — the host must click Start Live to
    // provision the real room. Until then viewers see a clear
    // scheduled/waiting state. The host's own control to Start Live
    // is rendered separately on the event card (not here).
    return {
      kind: "rgm_studio_pending",
      href: "#",
      label: "\u23F3 Scheduled \u2014 not started yet",
      disabled: true,
    };
  }

  if (status === "ended") {
    return { kind: kind, href: "#", label: "Event ended", disabled: true };
  }
  if (status === "upcoming") {
    if (kind === "livekit_room") {
      return { kind: kind, href: href, label: "\u23F3 Scheduled — opens live", disabled: false };
    }
    return { kind: kind, href: href, label: "\u23F3 View event", disabled: false };
  }
  return { kind: kind, href: href, label: label, disabled: disabled };
}

/**
 * React hook that returns a ticking `now` timestamp. By default it
 * updates every 15 seconds — fine-grained enough that an event
 * transitions at most ~15s after its scheduled boundary, cheap enough
 * that the viewer tab never hits the network for this behaviour.
 *
 *   • NEVER polls /api/live/events — the event list is unchanged.
 *   • NEVER mutates any live_rooms or live_events data.
 *   • Cleans the interval up when the component unmounts.
 */
function useEventClock(intervalMs) {
  const ms = Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : 15000;
  const [now, setNow] = React.useState(function () { return Date.now(); });
  React.useEffect(function () {
    const id = setInterval(function () { setNow(Date.now()); }, ms);
    return function () { clearInterval(id); };
  }, [ms]);
  return now;
}

module.exports = {
  computeEventStatus: computeEventStatus,
  resolveJoinAction: resolveJoinAction,
  hasBroadcastStarted: hasBroadcastStarted,
  isRsvpOpen: isRsvpOpen,
  useEventClock: useEventClock,
};

